import React from 'react';
import qs from 'qs';
import { action, observable } from 'mobx';
import { Area, Bar, Line } from 'recharts';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import Autocomplete from '@material-ui/lab/Autocomplete/Autocomplete';

import Tooltip from '@material-ui/core/Tooltip';
import { InfoIcon } from "../theme/AthenaIcons";
import Popover from '@material-ui/core/Popover';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';

import FilterList from '../store/FilterList';
import { chainAsyncCalls, debounce } from '../utils';


class BaseDashboardView extends React.Component {
  static apiSubPath = null;
  static model = null;

  @observable updating = true;
  @observable data = null;
  @observable filterList = null;
  @observable disabledSeries = [];
  @observable anchorInfoTooltip = null;

  constructor(props) {
    super(props);
    this.filterList = new FilterList({}, this.debouncedUpdateData);
  }

  async componentDidMount() {
    await this.onLocationChange();
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    if ((this.props.location !== prevProps.location) && !this.updating) {
      await this.onLocationChange();
    }
  }

  beforeLocationChange() {}

  @action onLocationChange = async () => {
    this.updating = true;
    this.beforeLocationChange();
    let data = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
    for (const [key, value] of Object.entries(data)) {
      if (value === '') {
        delete data[key];
      }
    }
    data = { ...this.getEmptyQueryParams(), ...data };
    await this.setData(data);
    for (let [filter, values] of Object.entries(data)) {
      if (!Array.isArray(values)) {
        values = [values];
      }
      for (const value of values) {
        this.filterList.checkValue(filter, value, false);
      }
    }
    await this.updateData();
  };

  async ajaxRequest(method, subPath, config) {
    return await this.constructor.model.ajaxRequest(method, subPath, config);
  }

  @action updateData = chainAsyncCalls(async () => {
    this.updating = true;
    try {
      const response = await this.ajaxRequest(
        'get',
        this.constructor.apiSubPath,
        { params: this.getQueryParams() },
      );
      this.data = response.data;
      await this.setData(response.data);
      if ('filters' in this.data) {
        this.filterList.update(this.data.filters);
      }
      this.props.history.replace('?' + this.getQueryString());
    } catch (error) {
      console.error(error);
      this.data = null;
    } finally {
      this.updating = false;
    }
  });

  debouncedUpdateData = debounce(this.updateData, 1000);

  _setData(data) {}

  @action setData(data) {
    this._setData({ ...data });
  }

  _getQueryParams() {
    return {};
  }

  getQueryParams() {
    return {
      ...this.filterList.filteredCheckedFilters,
      ...this._getQueryParams(),
    };
  }

  getEmptyQueryParams() {
    return Object.fromEntries(
      Object.entries(this.getQueryParams()).map(([key, value]) => [key, null])
    );
  }

  getQueryString(extra={}) {
    return qs.stringify(
      {...this.getQueryParams(), ...extra},
      { arrayFormat: 'repeat', skipNulls: true },
    );
  }

  get initialized() {
    return this.data !== null;
  }

  get ready() {
    return this.initialized && !this.updating;
  }

  fieldValue(value) {
    return value === null ? '' : value;
  }

  genericChangeHandler(name, update=true) {
    return action(event => {
      this[name] = this.fieldValue(event.target.value);
      if (update) {
        this.debouncedUpdateData();
      }
    });
  }

  genericChangeValueHandler(name, update=true) {
    return action((event, value) => {
      this[name] = this.fieldValue(value);
      if (update) {
        this.debouncedUpdateData();
      }
    });
  }

  genericToggleHandler(name, update=true) {
    return action(() => {
      this[name] = !this[name];
      if (update) {
        this.debouncedUpdateData();
      }
    });
  }

  renderAutocomplete(label, options, value, onChange, minWidth=150) {
    let option = options.filter(row => row.value === value);
    if (option.length === 0) {
      option = null;
    } else {
      option = option[0];
    }
    return (
      <Autocomplete
        options={[...options]}
        getOptionLabel={option => option.display_name}
        renderInput={params => <TextField {...params} label={label} variant="outlined" />}
        value={option}
        onChange={(event, option) => onChange(event, option.value)}
        style={{ minWidth }}
        disableClearable
      />
    );
  }

  renderChoicesField = (label, choices, value, onChange, minWidth=150) => (
    <TextField
      select
      label={label}
      value={this.fieldValue(value)}
      variant="outlined"
      onChange={onChange}
      style={{ minWidth }}
    >
      {choices.map(({ value, display_name }) =>
        <MenuItem key={value} value={value}>{display_name === null ? <em>Empty</em> : display_name}</MenuItem>
      )}
    </TextField>
  );


  renderMultiChoicesField = (label, choices, values, onChange, renderValue=null) => {
    values = values === null ? [] : values;
    values = typeof values === 'string' ? [values] : values;
    var itemCheck = {};
    choices.forEach(choice => {itemCheck[choice.value] = values.includes(choice.value)});

    if (renderValue === null){
      renderValue = (selected) => {
        selected = typeof selected === 'string' ? [selected] : selected ;
        return selected.join(', ') ;
      }
    }

    return (
      <TextField
        variant="outlined"
        label={label}
        select
        SelectProps={{ multiple: true, value: values, renderValue: renderValue, onChange: onChange }}
      >
        {choices.map(({value, display_name}) => (
          <MenuItem key={value} value={value}>
            <Checkbox checked={itemCheck[value]} />
            <ListItemText primary={display_name} />
          </MenuItem>
        ))}
      </TextField>
    );
  };

  getVerbose = (choices, value) => {
    return Object.fromEntries(
      choices.map(choice => [choice.value, choice.display_name])
    )[value];
  };

  @action onLegendClick = event => {
    const { dataKey } = event;
    if (this.disabledSeries.includes(dataKey)) {
      this.disabledSeries.remove(dataKey);
    } else {
      this.disabledSeries.push(dataKey);
    }
  };

  getLegendOpacity = seriesName => this.disabledSeries.includes(seriesName) ? 0.3 : 1;
  getOpacity = seriesName => this.disabledSeries.includes(seriesName) ? 0. : 1;

  legendFormatter = (value, entry, index) => {
    const opacity = this.getLegendOpacity(entry.dataKey);
    return (
      <span style={{ opacity, cursor: 'pointer' }}>
        {value}
      </span>
    );
  };

  getCommonChartProps = (dataKey, props) => {
    props = { dataKey, ...props };
    const opacity = this.getOpacity(dataKey);
    if (!('fillOpacity' in props)) {
      props.fillOpacity = 1;
    }
    props.fillOpacity *= opacity;
    if (!('strokeOpacity' in props)) {
      props.strokeOpacity = 1;
    }
    props.strokeOpacity *= opacity;
    return props;
  };

  getArea = (dataKey, props) => {
    return (
      <Area type="monotone" stroke={null} {...this.getCommonChartProps(dataKey, props)} />
    );
  };

  getBar = (dataKey, props) => (
    <Bar key={dataKey} {...this.getCommonChartProps(dataKey, props)} />
  );

  getLine = (dataKey, props) => (
    <Line type="monotone" dot={false} {...this.getCommonChartProps(dataKey, props)} />
  );

  getInfoTooltip = (infoMessage) => {
    const handleClick = event => {
      this.anchorInfoTooltip = event.currentTarget;
    };
    const handleClose = () => {
      this.anchorInfoTooltip = null;
    };
    const open = Boolean(this.anchorInfoTooltip);
    const id = open ? 'simple-popover' : undefined;

    return (
      <>
        <Tooltip title="Info">
          <IconButton onClick={handleClick}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
        <Popover
          id={id}
          open={open}
          anchorEl={this.anchorInfoTooltip}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          style={{ 'width': '100%', 'maxHeight': '80%' }}
        >
          <Box m={2}>
            {infoMessage}
          </Box>
        </Popover>
      </>
    );
  };


}


export default BaseDashboardView;
